import * as XLSX from "xlsx";

/** Matches Compass / Sprive bulk AML limits. */
export const AML_BULK_PPS_MAX =200;
export const AML_PPS_MIN_LEN = 12;
export const AML_PPS_MAX_LEN = 16;

const FORMAT_A_PPS_HEADER = "pps account number";

export type ParseBulkUploadResult = {
  pps_accounts: string[];
  duplicate_count: number;
  dropped_invalid: number;
  parse_skipped: number;
  format: "format_a_header" | "inferred_column";
  warnings: string[];
};

export function normalizePpsFromCell(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const text =
    typeof raw === "number" && Number.isFinite(raw)
      ? String(Math.trunc(raw))
      : String(raw).trim().replace(/^\t+/, "");
  if (!text) return null;

  const digitsOnly = text.replace(/\D/g, "");
  if (digitsOnly.length < AML_PPS_MIN_LEN || digitsOnly.length > AML_PPS_MAX_LEN) {
    return null;
  }
  if (digitsOnly.length !== text.replace(/[\s,]/g, "").length) {
    return null;
  }
  return digitsOnly;
}

function countDuplicateRows(pps_accounts: string[]): number {
  const seen = new Set<string>();
  let duplicate_count = 0;
  for (const digits of pps_accounts) {
    if (seen.has(digits)) duplicate_count += 1;
    else seen.add(digits);
  }
  return duplicate_count;
}

function capAndWarn(
  pps_accounts: string[],
  duplicate_count: number,
  parse_skipped: number,
  format: ParseBulkUploadResult["format"],
): ParseBulkUploadResult {
  const warnings: string[] = [];

  let capped = pps_accounts;
  if (pps_accounts.length > AML_BULK_PPS_MAX) {
    warnings.push(
      `Only the first ${AML_BULK_PPS_MAX} accounts in the file will be included.`,
    );
    capped = pps_accounts.slice(0, AML_BULK_PPS_MAX);
  }

  if (capped.length === 0) {
    throw new ParseBulkUploadError(
      "No valid account numbers found in this file.",
    );
  }

  return {
    pps_accounts: capped,
    duplicate_count,
    dropped_invalid: 0,
    parse_skipped,
    format,
    warnings,
  };
}

export class ParseBulkUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ParseBulkUploadError";
  }
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell);
      cell = "";
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
    } else {
      cell += char;
    }
  }

  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);

  return rows;
}

function findFormatAPpsColumnIndex(headerRow: string[]): number | null {
  const index = headerRow.findIndex(
    (cell) => cell.trim().toLowerCase() === FORMAT_A_PPS_HEADER,
  );
  return index >= 0 ? index : null;
}

function inferPpsColumnIndex(rows: string[][], sampleSize = 20): number | null {
  if (rows.length === 0) return null;

  const sample = rows.slice(0, sampleSize);
  const maxCols = sample.reduce((max, row) => Math.max(max, row.length), 0);
  if (maxCols === 0) return null;

  let bestIndex = -1;
  let bestScore = 0;

  for (let col = 0; col < maxCols; col += 1) {
    let score = 0;
    for (const row of sample) {
      if (normalizePpsFromCell(row[col])) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIndex = col;
    }
  }

  return bestScore > 0 ? bestIndex : null;
}

function extractPpsFromSheetRows(rows: unknown[][]): ParseBulkUploadResult {
  const asStrings = rows.map((row) =>
    (row ?? []).map((cell) =>
      cell === null || cell === undefined ? "" : String(cell).trim(),
    ),
  );

  if (asStrings.length === 0) {
    throw new ParseBulkUploadError("The file has no rows.");
  }

  const headerIndex = findFormatAPpsColumnIndex(asStrings[0] ?? []);
  let format: ParseBulkUploadResult["format"];
  let dataRows: string[][];
  let ppsCol: number;

  if (headerIndex !== null) {
    format = "format_a_header";
    ppsCol = headerIndex;
    dataRows = asStrings.slice(1);
  } else {
    format = "inferred_column";
    dataRows = asStrings;
    const inferred = inferPpsColumnIndex(dataRows);
    if (inferred === null) {
      throw new ParseBulkUploadError(
        "We couldn’t find a column of PPS account numbers in this file.",
      );
    }
    ppsCol = inferred;
  }

  const candidates: string[] = [];
  let parse_skipped = 0;

  for (const row of dataRows) {
    const cell = row[ppsCol];
    const pps = normalizePpsFromCell(cell);
    if (pps) {
      candidates.push(pps);
    } else if (cell?.trim()) {
      parse_skipped += 1;
    }
  }

  const duplicate_count = countDuplicateRows(candidates);
  return capAndWarn(candidates, duplicate_count, parse_skipped, format);
}

export function parseBulkUploadCsvText(text: string): ParseBulkUploadResult {
  const rows = parseCsvRows(text.replace(/^\uFEFF/, ""));
  return extractPpsFromSheetRows(rows);
}

export async function parseBulkUploadFile(file: File): Promise<ParseBulkUploadResult> {
  const name = file.name.toLowerCase();

  if (name.endsWith(".csv") || file.type === "text/csv") {
    const text = await file.text();
    return parseBulkUploadCsvText(text);
  }

  if (
    name.endsWith(".xlsx") ||
    name.endsWith(".xls") ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      throw new ParseBulkUploadError("The spreadsheet has no sheets.");
    }
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      raw: true,
      defval: "",
    }) as unknown[][];
    return extractPpsFromSheetRows(rows);
  }

  throw new ParseBulkUploadError("Please upload an Excel or CSV file.");
}
